import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PaginationRequest } from '../core/models/market-place/paginatedRequest';
import { UpdateProductRequest } from '../core/models/market-place/product/updateProductRequest';
import { BaseService } from '../core/services/shared/base.service';
import { ProductsFilteredRequest } from '../core/models/market-place/product/productsFilteredRequest';
import { ProductsByCategoryRequest } from '../core/models/market-place/product/productsByCategoryRequest';
import { Collection } from '../core/models/market-place/product/collection';
import { Product } from '../core/models/market-place/product/product';
import { ProductPaged } from '../core/models/market-place/product/productPaged';
import { ProductRequest } from '../core/models/market-place/product/productRequest';
import { ProductsByTagRequest } from '../core/models/market-place/product/productsByTagRequest';
import { Brand } from '../core/models/market-place/product/brand';
import { SubCategory } from '../core/models/market-place/product/subCategory';
import { ProductShortPagedDomain } from '../core/models/market-place/product/productShortPageResponse.model';
import { ProductsBySubCategoryRequest } from '../core/models/market-place/product/productsBySubCategoryRequest.model';
import { UserFavoriteProductRequest } from '../core/models/market-place/product/userFavoriteProductRequest';
import { AppyUser } from '../core/model/v2/AppyUser';
import { UserService } from '../core/services/v2/user.service';
import { ProductShortDomain } from '../core/models/market-place/product/productShort.model';
import { ShoppingCartReturnDomain } from '../core/models/market-place/product/shoppingCartReturn.model';
import { ProductByCriteriaRequest } from '../core/models/market-place/product/productByCriteriaRequest.model';
import { FilterCriteria } from '../core/models/market-place/product/filterCriteria.model';
import { AddProductToShoppingCart } from '../core/models/market-place/product/addProductToCartRequest.model';
import { BreadCrumbDetail } from '../core/models/market-place/product/bread-crumb.model';
import { ShippingMethodDomain, ShippingCostRequestDomain } from '../core/models/market-place/product/shippingMethod.model';
import { CreateOrderRequest } from '../core/models/market-place/product/createOrderRequest.model';
import { CreateProductReviewRequest } from '../core/models/market-place/product/createProductReviewRequest.model';
import { Order } from '../core/models/market-place/product/order.model';
import { OrderShortDomain } from '../core/models/market-place/product/orderShortDomain.model';
import { ResultBase } from '../core/models/result-base';
import { ProductPromocodeRequest,ProductPromocodeResponse } from '../core/models/market-place/product/productPromocode';
import { OrderStatusShortDomain } from '../core/models/market-place/product/OrderStatusShortDomain';
import { Promocode } from '../core/models/market-place/product/promocode';

@Injectable({
  providedIn: 'root'
})
export class MarketPlaceService {

  //#region PROPERTIES
  isDisplayFilterSection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  allCategories: BehaviorSubject<Collection[]> = new BehaviorSubject<Collection[]>([]);
  loggedInUser: BehaviorSubject<AppyUser> = new BehaviorSubject<AppyUser>(null);
  allSubCategories: BehaviorSubject<SubCategory[]> = new BehaviorSubject<SubCategory[]>([]);
  filterCriteria: BehaviorSubject<FilterCriteria> = new BehaviorSubject<FilterCriteria>(null);

  categoryName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  subCategoryName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  productName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  discount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  searchName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  shoppingCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  favoriteProduct: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  searchOrderById: BehaviorSubject<string> = new BehaviorSubject<string>(null);
 
  shoppingCartInfo: BehaviorSubject<ShoppingCartReturnDomain[]> = new BehaviorSubject<ShoppingCartReturnDomain[]>([]);
  shoppingCartInfoChange: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  //#endregion
  //#region CONSTRUCTOR
  constructor(private httpClient: HttpClient, private baseService: BaseService, private userService: UserService) { }
  //#endregion

  //#region product endpoints
  getProductById(productId: Number): Observable<Product[]> {
    return this.baseService.get<Product[]>('Product/GetProduct?pId=' + productId);
  }

  getAllProducts(request: PaginationRequest): Observable<ProductPaged[]> {
    return this.baseService.post<ProductPaged[]>('Product/GetAllProducts', request);
  }

  addProduct(request: ProductRequest): Observable<Product> {
    return this.httpClient.post<Product>('/api/get?url=Product/AddProduct', request);
  }

  updateProduct(request: UpdateProductRequest): Observable<any> {
    return this.httpClient.post<any>('/api/get?url=Product/UpdateProduct', request);
  }

  getProductByCategory(tag: string): Observable<Product[]> {
    return this.baseService.get<Product[]>(encodeURI('Product/GetProductsByCategory?pTag=' + tag));
  }

  getProductWithoutCollection(): Observable<Product[]> {
    return this.httpClient.get<Product[]>('/api/get?url=Product/GetProductWithoutCollection');
  }

  getProductsByTag(request: ProductsByTagRequest): Observable<ProductShortPagedDomain> {
    return this.baseService.post<ProductShortPagedDomain>('Product/GetProductsByTag', request);
  }

  getListOfCategory(): Observable<Collection[]> {
    return this.baseService.get<Collection[]>('Product/GetListOfCategory');
  }

  getProductsByCategory(request: ProductsByCategoryRequest): Observable<ProductShortPagedDomain> {
    return this.baseService.post<ProductShortPagedDomain>('Product/GetProductsByCategory', request);
  }

  getProductsFiltered(request: ProductsFilteredRequest): Observable<ProductShortPagedDomain> {
    return this.baseService.post<ProductShortPagedDomain>('Product/GetProductsFiltered', request);
  }

  getPromocode(request: ProductPromocodeRequest): Observable<ProductPromocodeResponse> {
    return this.baseService.post<ProductPromocodeResponse>('Product/ApplyPromoCode', request);
  }

  getPromocodesList(): Observable<Promocode[]> {
    return this.baseService.get<Promocode[]>('Product/GetPromoCodeList');
  }
  getListOfBrands(): Observable<Brand[]> {
    return this.baseService.get<Brand[]>('Product/GetListOfBrands');
  }

  getListOfSubCategory(categoryId: Number): Observable<SubCategory[]> {
    return this.baseService.get<SubCategory[]>('Product/GetListOfSubCategory?pCategoryId=' + categoryId);
  }

  getProductsBySubCategory(request: ProductsBySubCategoryRequest): Observable<ProductShortPagedDomain> {
    return this.baseService.post<ProductShortPagedDomain>('Product/GetProductsBySubCategory', request);
  }

  addUserFavoriteProduct(request: UserFavoriteProductRequest): Observable<any> {
    return this.baseService.post<any>('Product/AddUserFavoriteProduct', request);
  }

  createProductReview(request: CreateProductReviewRequest): Observable<any> {
    return this.baseService.post<any>('Product/CreateProductReview', request);
  }

  getUserFavoriteProducts(userId: Number): Observable<ProductShortDomain[]> {
    return this.baseService.get<ProductShortDomain[]>('Product/GetUserFavoriteProducts?pUserId=' + userId);
  }

  getShoppingCart(userId: Number): Observable<ShoppingCartReturnDomain[]> {
    return this.baseService.get<ShoppingCartReturnDomain[]>('Product/GetShoppingCart?pUserId=' + userId);
  }

  addProductToShoppingCart(request: AddProductToShoppingCart): Observable<any> {
    return this.baseService.post<any>('Product/AddShoppingCart', request);
  }

  removeProductFromCart(request: AddProductToShoppingCart): Observable<any> {
    return this.baseService.post<any>('Product/RemoveItemFromShoppingCart', request);
  }

  getProductByCriteria(request: ProductByCriteriaRequest): Observable<ProductShortDomain[]> {
    return this.baseService.post<ProductShortDomain[]>('Product/GetProductByCriteria', request);
  }

  gePromoCodeDetails(request: ProductPromocodeRequest): Observable<ProductShortDomain[]> {
    return this.baseService.post<ProductShortDomain[]>('Product/GetProductByCriteria', request);
  }

  getRelatedProductsByProductId(userId: Number, productId: number): Observable<ProductShortDomain[]> {
    return this.baseService.get<ProductShortDomain[]>('Product/GetRelatedProductsByProductId?pProductId=' + productId + '&pUserId=' + userId);
  }

  getProductShippingMethods(): Observable<ShippingMethodDomain[]> {
    return this.baseService.get<ShippingMethodDomain[]>('Product/GetProductShippingMethods');
  }

  createOrder(request: CreateOrderRequest): Observable<ResultBase<boolean>> {
    return this.baseService.post<ResultBase<boolean>>('Product/CreateOrder', request);
  }

  getProductOrders(userId: Number, orderId?: number): Observable<OrderShortDomain[]> {
    return this.baseService.get<OrderShortDomain[]>('Product/GetProductOrders?pUserId=' + userId + '&pOrderId?=' + orderId);
  }
  getOrderStatus(userId: Number, orderId: string): Observable<OrderStatusShortDomain[]> {
    return this.baseService.get<OrderStatusShortDomain[]>('Product/GetOrderStatus?pUserId=' + userId + '&pOrderId=' + orderId);
  }
  getLoggedInUserDetails(): Promise<AppyUser> {
    return new Promise<AppyUser>((resolve, reject) => {
      let user = this.loggedInUser.getValue();
      if (user == null) {
        this.userService.getUserDetails().subscribe((res: AppyUser) => {
          this.loggedInUser.next(res);
          resolve(res);
        }, reject);
      } else {
        resolve(user);
      }
    });
  }

  getShoppingCartInfo(): Promise<ShoppingCartReturnDomain[]> {
    return new Promise<ShoppingCartReturnDomain[]>(async (resolve, reject) => {
      let user = await this.getLoggedInUserDetails();
      let cartInfo = this.shoppingCartInfo.getValue();
      if (user != null && user != undefined && cartInfo.length == 0) {
        this.getShoppingCart(user.id).subscribe((res: ShoppingCartReturnDomain[]) => {
          this.shoppingCartInfo.next(res);
          resolve(res);
        }, reject);
      } else {
        resolve(cartInfo);
      }
    });
  }
 
  //getDiscountPrice(): Promise<number> {
  //  return new Promise<number>(async (resolve, reject) => {
  //    resolve(this.discount);
  //  });
  //}



  //#region MANAGE BREAD-CRUMBS

  setBreadCrumb(breadCrumb: BreadCrumbDetail) {
    this.removeBreadCrumb();
    localStorage.setItem('breadCrumb', JSON.stringify(breadCrumb));
  }

  getBreadCrumb(): BreadCrumbDetail {
    let breadCrumbDetail = localStorage.getItem('breadCrumb');
    return JSON.parse(breadCrumbDetail);
  }

  removeBreadCrumb() {
    localStorage.removeItem('breadCrumb');
  }

  //#endregion

  removeFilterData(){
    localStorage.removeItem('filteredQuery');
  }


  getShippingCost(request: ShippingCostRequestDomain): Observable<{shippingCost: number}> {
    return this.baseService.post<{shippingCost: number}>('Product/GetShippingCost', request);
  }

  //#endregion
}
